import React from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Stack,
} from '@mui/material';

interface IAnswer {
  answerText: string;
  explanation?: string | null;
}

interface IQuestion {
  id: any;
  questionText: string;
  answers: IAnswer[];
  options: string[];
  type: string;
  correctAnswer: string[];
}

interface AIQuizDisplayProps {
  value: string | IQuestion[];
  placeholder: string;
}

const AIQuizDisplay: React.FC<AIQuizDisplayProps> = ({
  value,
  placeholder,
}) => {
  let questions: IQuestion[] = [];

  try {
    if (typeof value === 'string') {
      const trimmedVal = value
        .trim()
        .replace(/^```json\s*/i, '')
        .replace(/^```\s*/i, '')
        .replace(/\s*```$/i, '');
      questions = JSON.parse(trimmedVal);
    } else {
      questions = value;
    }
  } catch (err) {
    return (
      <Typography color="error">
        Failed to parse AI quiz response.
      </Typography>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Typography color="textSecondary">
        {placeholder ?? 'No quiz questions generated yet.'}
      </Typography>
    );
  }

  return (
    <Box>
      {questions.map((q) => (
        <Box key={q.id} sx={{ mb: 4 }}>
          {/* Question text */}
          <TextField
            fullWidth
            value={q.questionText}
            InputProps={{ readOnly: true }}
            variant="outlined"
            sx={{ mb: 2 }}
          />

          {/* Multiple choice answers */}
          <RadioGroup value={q.correctAnswer[0] ?? ''}>
            {q.options.map((option, i) => {
              const answer = q.answers.find((a) => a.answerText === option);
              return (
                <Stack
                  // eslint-disable-next-line
                  key={i}
                  direction="column"
                  spacing={1}
                  sx={{ mb: 1 }}
                >
                  <FormControlLabel
                    value={option}
                    control={<Radio disabled />}
                    label={(
                      <TextField
                        value={option}
                        InputProps={{ readOnly: true }}
                        variant="outlined"
                        size="small"
                      />
                    )}
                  />
                  {/* Only show explanation if it exists */}
                  {answer?.explanation && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ ml: 5 }}
                    >
                      {answer.explanation}
                    </Typography>
                  )}
                </Stack>
              );
            })}
          </RadioGroup>
        </Box>
      ))}
    </Box>
  );
};

export default AIQuizDisplay;
